import type { PropsWithChildren } from "react";
import { downloadFile, type PartialFileUpload } from "../utils";
import { Button } from "./button";
import { Icon } from "./icon";

export const getFilePreviewNavigation = <T extends PartialFileUpload>(
  files: T[],
  selectedFile: T,
  setSelectedFile: (file?: T) => void
) => {
  const index = files.findIndex((file) => file.id === selectedFile.id);
  const previousFile = index > 0 ? files[index - 1] : undefined;
  const nextFile = index >= 0 ? files[index + 1] : undefined;

  return {
    close: () => setSelectedFile(undefined),
    download: () => void downloadFile(selectedFile),
    goToNext: () => nextFile && setSelectedFile(nextFile),
    goToPrevious: () => previousFile && setSelectedFile(previousFile),
    hasNextFile: nextFile !== undefined,
    hasPreviousFile: previousFile !== undefined,
    index,
  };
};

export const FilePreviewNav = <T extends PartialFileUpload>({
  children,
  files,
  selectedFile,
  setSelectedFile,
}: PropsWithChildren & {
  files: T[];
  selectedFile: T;
  setSelectedFile: (file?: T) => void;
}) => {
  if (!files.length) {
    return null;
  }

  const { close, download, goToNext, goToPrevious, hasNextFile, hasPreviousFile } = getFilePreviewNavigation(
    files,
    selectedFile,
    setSelectedFile
  );

  return (
    <nav className="absolute bg-popover bottom-2 flex gap-2 items-center left-1/2 outline outline-background-contrast p-1 rounded-full shadow-xl -translate-x-1/2">
      <Button
        variant="outline"
        size="sm"
        display="icon"
        className="rounded-full"
        onClick={goToPrevious}
        disabled={!hasPreviousFile}
      >
        <Icon icon="chevron_left" />
      </Button>
      {children}
      {/* The preview renders from a blob URL, so the browser's own save button names the file after the blob's UUID. */}
      <Button
        variant="ghost"
        size="sm"
        display="icon"
        className="rounded-full"
        aria-label="Download"
        onClick={download}
      >
        <Icon icon="download" />
      </Button>
      <Button variant="ghost" size="sm" display="icon" className="rounded-full" onClick={close}>
        <Icon icon="close_fullscreen" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        display="icon"
        className="rounded-full"
        onClick={goToNext}
        disabled={!hasNextFile}
      >
        <Icon icon="chevron_right" />
      </Button>
    </nav>
  );
};
